import Answer from "./Answer";
import { Validation, DateValidator, NumberValidator, TextValidator, RegexValidator } from "../Validation";
import RuleSet from "../Rules/RuleSet";
import { QuestionCodes } from "../UserJourney/UserJourneyMap";
import { normalizePostcodeValue } from "./postcodeKeyboard";
import { getPostcodeRegexForLocale, getPostcodeErrorKeyForLocale } from "./postcodeRegexByLocale";

export default class Question {
    /**
     *
     * @param {{}}schema
     * @param questionGroup {QuestionGroup}
     */
    constructor(schema, questionGroup) {
        this._schema = schema;
        this._questionGroup = questionGroup;
        this._answers = [];
        this._questionStates = {
            notAsked: "notAsked", // never been evaluated. indicates the user never reached this question
            didNotQualify: "didNotQualify", // question rules stopped this question being asked
            answered: "answered", // user provided an answer
            answersCleared: "answersCleared", // answers where provided but removed
            answerPreFilled: "answerPreFilled"
        };
        this._questionState = this._questionStates.notAsked;
        this._previousQuestion = null;
        this._nextQuestion = null;
        this._preConditionRuleSet = null;
        this._exitConditionRuleSet = null;
    }

    /**
     * Set the answer of the question from the associated Response.
     * Due to the the Survey evaluation process we can not set the the state of the question to notAsked if there is no previous answer. see SU-965
     *
     * @returns {Question}
     */
    prePopulateWithResponse() {
        if (!this.isStateAnswered() && !this.isStateDidNotQualify()) {
            /* istanbul ignore else */
            let responseUtility = this._questionGroup.getSurvey().getResponse();
            if (responseUtility) {
                let previousAnswers = responseUtility.getAnswersForPreFill();

                if (previousAnswers.hasOwnProperty(this.getCode())) {
                    // Copy the array — never adopt another question's live _answers array by
                    // reference. previousAnswers values are the live _answers arrays of other
                    // same-code questions (Response._extractAnswersFromSurveyForQuestionsWithACode
                    // hands them out directly). Aliasing here let a later addAnswer() push into a
                    // sibling question's array (e.g. the NHS_ETHNICITY carrier shared by the
                    // UK_ETHNICITY and QRISK3 surveys), duplicating answers on both.
                    this._answers = [...previousAnswers[this.getCode()]];
                    this._questionState = this._questionStates.answerPreFilled;
                }
            }
        }

        return this;
    }

    /**
     *
     * @returns {bool}
     */
    getAnswersContainPII() {
        return this._schema.answersContainPII;
    }

    // /**
    //  * TODO: REALLY QUESTIONING NEED FOR THIS... IF preconditions exist and they fail, then it can't be asked. Answer will not be found in Response
    //  * Returns if this question's preconditions have enough for answers
    //  *
    //  * @return {Boolean}
    //  * @private
    //  */
    // _allPreConditionQuestionsHaveAnswers() {
    //
    //     let allPreConditionQuestionsHaveAnswers = true;
    //     /** @type {Object|null|undefined} preConditions */
    //     const preConditions = this._schema.preConditions;
    //
    //     if (preConditions) {
    //
    //         for (let questionCode in preConditions) {
    //
    //             let questionModel = this._questionGroup.getSurvey().getQuestionByCode(questionCode);
    //
    //             // do we have enough information to evaluate anyway
    //             if (!questionModel || (!questionModel.hasStateAnsweredByPreFill() && !questionModel.hasStateAnswered())) {
    //                 allPreConditionQuestionsHaveAnswers = false;
    //                 break; // no need to continue search
    //             }
    //         }
    //     }
    //
    //     return allPreConditionQuestionsHaveAnswers;
    // }

    /**
     * Returns if this question's pre-conditions succeed or fail
     *
     * @return {Boolean}
     */
    canBeAsked() {
        if (!this._preConditionRuleSet) {
            this._preConditionRuleSet = RuleSet.createFromAnswerRuleSchema(
                this._schema.preConditions,
                this._questionGroup.getSurvey().getResponse(),
                { noRulesResult: true }
            );
        }

        // NB: trying to understand why blue pre condition from schema is failing in storybook
        return this._preConditionRuleSet.matches();
    }

    /**
     * Set the state of the question to notAsked
     */
    setStateNotAsked() {
        this._setQuestionState(this._questionStates.notAsked);
    }

    setStateDidNotQualify() {
        this._setQuestionState(this._questionStates.didNotQualify);
    }

    /**
     *
     * @param state
     * @returns {Question}
     * @private
     */
    _setQuestionState(state) {
        this._questionState = state;
        return this;
    }

    /**
     * Does this Question have the given state? Create hasAStateXXX() functions for public interfacing
     *
     * @param state
     * @returns {boolean}
     * @private
     */
    _isQuestionState(state) {
        return this._questionState === state;
    }

    /**
     * The default state. Implies that User has been asked this question yet. Also means that the qualifying conditions
     * have not been assessed yet
     *
     * @returns {boolean}
     */
    isStateNotAsked() {
        return this._isQuestionState(this._questionStates.notAsked);
    }

    /**
     * Question conditions means the user could not be asked this question
     *
     * @returns {boolean}
     */
    isStateDidNotQualify() {
        return this._isQuestionState(this._questionStates.didNotQualify);
    }

    /**
     * Question state where this question has been answered previously and has answered this instance of it
     *
     * @returns {boolean}
     */
    isStateAnsweredByPreFill() {
        return this._isQuestionState(this._questionStates.answerPreFilled);
    }

    /**
     * Returns if the user has provided an answer to this Question
     * @returns {boolean}
     */
    isStateAnswered() {
        return this._isQuestionState(this._questionStates.answered);
    }

    /**
     * Means the user has replied but has also changed their answer, yet didn't answer it again (likely represents a programmatic error)
     * @returns {boolean}
     */
    isStateAnswersCleared() {
        return this._isQuestionState(this._questionStates.answersCleared);
    }

    /**
     * Returns the question type determined in the schema. Create isQuestionTypeXXXX() for public interfacing
     *
     * @param code
     * @returns {boolean}
     * @private
     */
    _isQuestionType(code) {
        return this._schema.surveyQuestionType.code.toLowerCase() === code.toLowerCase();
    }

    /**
     * Is a Text type Question
     * @returns {boolean}
     */
    isQuestionTypeText() {
        return this._isQuestionType("TEXT");
    }

    /**
     * Is a Number type Question
     * @returns {boolean}
     */
    isQuestionTypeNumber() {
        return this._isQuestionType("NUMBER");
    }

    /**
     * Is a Choice type Question
     * @returns {boolean}
     */
    isQuestionTypeChoice() {
        return this._isQuestionType("CHOICE");
    }

    isQuestionTypeMultipleAnswer() {
        return this._isQuestionType("MULTIPLE_ANSWER");
    }

    /**
     * Is a Date type Question
     * @returns {boolean}
     */
    isQuestionTypeDate() {
        return this._isQuestionType("DATE");
    }

    /**
     * Is a Barcode type Question
     * @returns {boolean}
     */
    isQuestionTypeBarcode() {
        return this._isQuestionType("BARCODE");
    }

    /**
     * Returns if this Question has reached a resolution as part of the user journey by either being answered or not qualifying to be answered
     *
     * @returns {boolean}
     */
    hasBeenResolved() {
        return this.hasBeenAnswered() || this.isStateDidNotQualify();
    }

    /**
     * Returns if this Question has been answered by the user or from a 'previous' set of answers
     *
     * @returns {boolean}
     */
    hasBeenAnswered() {
        // evaluate if this answer has already been answered
        this.prePopulateWithResponse();
        return this.isStateAnswered() || this.isStateDidNotQualify() || this.isStateAnsweredByPreFill();
    }

    /**
     * Adds an Answer to the answer array for this Question
     *
     * @param answer - The answer value to add
     * @param t - translation function (optional) - passed from React components to enable localized validation errors
     * @returns {boolean|string[]} - true if valid and added, array of error messages if invalid
     */
    addAnswer(answer, t = null) {
        // Canonicalise POSTAL_CODE values to the station locale's spaced form before validating
        // and storing (e.g. "sw1a1aa" -> "SW1A 1AA"). Done before validation so an unspaced but
        // valid postcode passes; the stored answer is the canonical form.
        if (this.getCode() === QuestionCodes.postalCode) {
            answer = normalizePostcodeValue(answer, this._getCampaignLocaleTag());
        }

        const outcome = this.validateAnswer(answer, t);

        // More explicit validation check
        if (outcome === true) {
            // A prefilled answer is provisional — the copy carried over from a same-code
            // question in an earlier survey (e.g. the NHS_ETHNICITY carrier shared by
            // UK_ETHNICITY and QRISK3). An explicit addAnswer is a real answer for THIS
            // question, so it supersedes the prefill rather than appending to it; otherwise
            // the carrier ends up with the prefilled value plus the added one (["Irish","Irish"]).
            if (this._questionState === this._questionStates.answerPreFilled) {
                this._answers = [];
            }

            // Only proceed if validation explicitly passes
            this._answers.push(new Answer(answer));
            this._questionState = this._questionStates.answered;

            if (this._hasTriggeredExitCondition()) {
                this.getQuestionGroup()
                    .getSurvey()
                    .setCompleteByQuestionExitCondition();
            } else {
                this.getQuestionGroup()
                    .getSurvey()
                    .evaluateSurveyState();
            }
            return true;
        } else {
            // outcome is an array of validation errors
            return outcome;
        }
    }

    addMultipleAnswers(answers) {
        if (!Array.isArray(answers) || answers.length === 0) {
            return [`invalid type for answers: ${typeof answers}`];
        }

        // TODO: revisit to see how we are supposed to deal with the validation
        for (let answer of answers) {
            this._answers.push(new Answer(answer));
            this._questionState = this._questionStates.answered;
        }

        if (this._hasTriggeredExitCondition()) {
            this.getQuestionGroup()
                .getSurvey()
                .setCompleteByQuestionExitCondition();
        } else {
            this.getQuestionGroup()
                .getSurvey()
                .evaluateSurveyState();
        }

        return true;
    }

    /**
     *
     * @returns {*|boolean}
     * @private
     */
    _hasTriggeredExitCondition() {
        if (!this._exitConditionRuleSet) {
            this._exitConditionRuleSet = RuleSet.createFromAnswerRuleSchema(
                this._schema.exitConditions,
                this._questionGroup.getSurvey().getResponse()
            );
        }

        return this._exitConditionRuleSet.matches();
    }

    /**
     * Validates an answer value using configured validators
     *
     * @param value - The value to validate
     * @param t - translation function (optional) - enables localized validation error messages
     * @return {boolean|string[]} - true if valid, array of error messages if invalid
     */
    validateAnswer(value, t = null) {
        const configuration = this.getValidationConfiguration();
        const validationUtility = new Validation();

        switch (true) {
            case this.isQuestionTypeDate():
                validationUtility.addValidator(new DateValidator(configuration));
                validationUtility.addValidator(new RegexValidator(configuration));
                break;

            case this.isQuestionTypeNumber():
                validationUtility.addValidator(new NumberValidator(configuration));
                validationUtility.addValidator(new RegexValidator(configuration));
                break;

            case this.isQuestionTypeText():
                validationUtility.addValidator(new TextValidator(configuration));
                validationUtility.addValidator(new RegexValidator(configuration));
                break;

            case this.isQuestionTypeChoice():
                validationUtility.addValidator(new TextValidator(configuration));
                validationUtility.addValidator(new RegexValidator(configuration));
                break;
        }

        // KEY: Pass translation function to validation system for i18n error messages
        const errors = validationUtility.validate(value, configuration.isRequired, t);
        return errors.length === 0 ? true : errors;
    }

    /**
     *
     * @returns {boolean}
     */
    isRequired() {
        return this.getValidationConfiguration().isRequired === true;
    }

    /**
     * Deletes all previously captured answers
     *
     * @returns {Question}
     */
    clearAnswers(evaluateSurveyState = true) {
        this._answers = [];
        this._questionState = this._questionStates.answersCleared;
        if (evaluateSurveyState) {
            this.getQuestionGroup()
                .getSurvey()
                .evaluateSurveyState();
        }
        return this;
    }

    getAnswers() {
        return this._answers;
    }

    /**
     *
     * @param {Answer[]} answers
     */
    setAnswers(answers) {
        this._answers = answers;
    }

    /**
     * Set a single provisional (prefilled) answer, replacing any current answers. Uses a fresh
     * array (never aliases a caller's array) and marks the question answerPreFilled so the
     * response's answer extraction includes it. Bypasses validation and survey re-evaluation on
     * purpose — used by finalize-time backfill of resolved cross-survey data (e.g. ethnicity).
     *
     * @param {*} value - the raw answer value
     * @returns {Question}
     */
    setPrefilledAnswer(value) {
        this._answers = [new Answer(value)];
        this._questionState = this._questionStates.answerPreFilled;
        return this;
    }

    getHelpText() {
        return this._schema.helpText;
    }

    getDisplayText() {
        return this._schema.displayText;
    }

    getQuestionId() {
        return this._schema.surveyQuestionId;
    }

    getPosition() {
        return this._schema.position;
    }

    getChoices() {
        let choices = [];
        if (this.isQuestionTypeChoice() || this.isQuestionTypeMultipleAnswer()) {
            choices = this._schema.configuration.choice.choices;
        }

        return choices;
    }

    getChoiceSize() {
        return this._schema.configuration.choice.size;
    }

    /**
     * Get the display options
     * @returns {string}
     */
    _getDisplayOption() {
        return this.isQuestionTypeChoice() || this.isQuestionTypeMultipleAnswer()
            ? this._schema.configuration.displayOption
            : "";
    }

    /**
     * Get the question icons. hasOwnProperty is in play while not all schemas have this config key
     *
     * @returns {string} or null
     */
    getQuestionIcon() {
        return this._schema.configuration.hasOwnProperty("icon") ? this._schema.configuration.icon : null;
    }

    getCode() {
        return this._schema.code;
    }

    /**
     * Returns the validation regex pattern for this question, or null when none is
     * configured. For POSTAL_CODE this is the resolved pattern — the survey's own config regex
     * if present, else the station-locale pattern (see getValidationConfiguration) — so callers
     * can test user input against the same regex used at submit time.
     *
     * @returns {string|null}
     */
    getRegexPattern() {
        return this.getValidationConfiguration().regexPattern || null;
    }

    getValidationConfiguration() {
        const defaultConfiguration = {
            isRequired: true
        };

        let configuration = { ...defaultConfiguration };

        if (this._schema.configuration.hasOwnProperty("validation")) {
            configuration = {
                ...configuration,
                ...this._schema.configuration.validation
            };
        }

        // POSTAL_CODE regex resolution (scoped to POSTAL_CODE so other questions are untouched).
        // Precedence:
        //   1. the survey's OWN config regex (configuration.validation.regexPattern, already spread
        //      in above) wins if present & non-empty. This lets an operator set ".*" to accept
        //      postcodes from any country (e.g. an Italy station serving foreign visitors). The
        //      config regex is used as-is (no repair). Error message: the survey's own
        //      regexErrorMessage if it has one, else a generic postcode key.
        //   2. otherwise fall back to the STATION locale (installation-country locale, see
        //      _getCampaignLocaleTag): that locale's regex + i18n error key.
        //   3. otherwise (no config regex, and no/"en"/unknown station locale): no validation —
        //      the regex is stripped, so the frontend shows the standard Latin keyboard and any
        //      input is accepted.
        // validateAnswer reads this (via the resolved regexPattern). The on-screen keyboard is
        // NOT derived from here — see getPostcodeKeyboardRegex (a config regex always gets the
        // full alphanumeric keyboard; only the station-locale path drives numpad/mask).
        if (this.getCode() === QuestionCodes.postalCode) {
            if (this._hasOwnPostcodeRegex()) {
                if (!configuration.regexErrorMessage) {
                    configuration.regexErrorMessage = "surveys.regex_error.post_code";
                }
            } else {
                const stationLocaleTag = this._getCampaignLocaleTag();
                const localeRegex = stationLocaleTag ? getPostcodeRegexForLocale(stationLocaleTag) : null;
                if (localeRegex) {
                    configuration.regexPattern = localeRegex;
                    configuration.regexErrorMessage = getPostcodeErrorKeyForLocale(stationLocaleTag);
                } else {
                    delete configuration.regexPattern;
                }
            }
        }

        return configuration;
    }

    /**
     * Whether this question carries its own postcode regex in config
     * (configuration.validation.regexPattern present & non-empty). Priority #1 for POSTAL_CODE
     * validation — see getValidationConfiguration.
     *
     * @returns {boolean}
     * @private
     */
    _hasOwnPostcodeRegex() {
        const validation = this._schema.configuration && this._schema.configuration.validation;
        return Boolean(
            validation && typeof validation.regexPattern === "string" && validation.regexPattern.trim() !== ""
        );
    }

    /**
     * The regex the on-screen keyboard should derive from (numpad + auto-separator mask vs the
     * full alphanumeric keyboard). This is deliberately NOT the validation regex:
     *   - a survey with its OWN config regex always gets the full alphanumeric keyboard (returns
     *     null), so authors never have to craft a regex to control the keyboard — the config regex
     *     only drives validation (see getRegexPattern);
     *   - otherwise the station-locale regex drives the keyboard (numpad for numeric locales), or
     *     null when there is no/unknown/generic-"en" station locale.
     *
     * @returns {string|null}
     */
    getPostcodeKeyboardRegex() {
        if (this.getCode() !== QuestionCodes.postalCode || this._hasOwnPostcodeRegex()) {
            return null;
        }
        return this.getRegexPattern();
    }

    /**
     * The locale languageTag from the active Response configuration (e.g. "pl_PL"), or null
     * when unavailable (e.g. rendered outside a health-check journey). This is `config.locale`
     * — the same value as `apiClient.locale` on the station — which the API derives from the
     * country of the station's current installation (HealthStation → installation → address →
     * country → locale), NOT from the campaign or the survey. It is the FALLBACK source for the
     * POSTAL_CODE regex + error message, used only when the question carries no config regex of
     * its own (the survey's own config regex takes priority — see getValidationConfiguration).
     *
     * @returns {string|null}
     * @private
     */
    _getCampaignLocaleTag() {
        const survey = this._questionGroup && this._questionGroup.getSurvey ? this._questionGroup.getSurvey() : null;
        const response = survey && survey.getResponse ? survey.getResponse() : null;
        const configuration = response && response.configuration ? response.configuration : null;
        return configuration && configuration.locale ? configuration.locale.languageTag : null;
    }

    /**
     *
     * @returns {QuestionGroup}
     */
    getQuestionGroup() {
        return this._questionGroup;
    }

    /**
     *
     * @returns {Object}
     */
    toJSON() {
        return {
            schema: this._schema,
            questionStates: this._questionStates,
            questionState: this._questionState,
            answers: this._answers
        };
    }

    /**
     * Update this instance with the given JSON. Assumes JSON is of the correct format
     *
     * @param questionJSON
     */
    fromJSON(questionJSON) {
        // if (questionJSON.schema.code === 'DATE_OF_BIRTH') {
        //     console.log("memememememe");
        // }
        for (let a = 0; a < questionJSON.answers.length; a++) {
            let answerObject = questionJSON.answers[a];
            // when replaying PII Question we still need to create the answer record despite the answer being null
            // #chickenEgg (need Response utility generated from the Response model data before populating with the PII DTO)
            // FIXME: ideally not ignored. Need Response mock data with PII scrubbed
            if (this.getAnswersContainPII()) {
                let piiAnswer = new Answer(answerObject.answer);
                piiAnswer.piiScrubbed = answerObject.piiScrubbed;
                this._answers.push(piiAnswer);
            } else {
                this._answers.push(new Answer(questionJSON.answers[a].answer));
                //this.addAnswer(questionJSON.answers[a].answer);
            }
        }

        this._setQuestionState(questionJSON.questionState);
    }

    /**
     *
     * @param {Question} question
     */
    set previousQuestion(question) {
        if (!this._previousQuestion || this._previousQuestion.getQuestionId() !== question.getQuestionId()) {
            this._previousQuestion = question;
            question.nextQuestion = this;
        }
    }

    /**
     *
     * @returns {Question|null}
     */
    get previousQuestion() {
        return this._previousQuestion;
    }

    /**
     *
     * @param {Question} question
     */
    set nextQuestion(question) {
        if (!this._nextQuestion || this._nextQuestion.getQuestionId() !== question.getQuestionId()) {
            this._nextQuestion = question;
            question.previousQuestion = this;
        }
    }

    get nextQuestion() {
        return this._nextQuestion;
    }

    isDisplayOptionStacked() {
        return this._getDisplayOption() === "stacked";
    }

    isDisplayOptionSideBySide() {
        return this._getDisplayOption() === "sidebyside";
    }

    /**
     * Body layout — buttons arranged on a human body silhouette.
     * @returns {boolean}
     */
    isDisplayOptionBody() {
        return this._getDisplayOption() === "body";
    }

    isDisplayOptionRow() {
        return this._getDisplayOption() === "row";
    }

    /**
     * Numeric scale — a row of small, evenly spaced circles holding digits, captioned at the ends.
     *
     * Kept deliberately narrow: this renders numbers and nothing else (0-10 presenteeism, 1-10 MSK
     * severity). A scale whose points are words wants `bubble` instead, which sizes the circle to
     * its copy. Splitting them is what lets this one stay simple rather than growing a mode for
     * every new shape of scale.
     * @returns {boolean}
     */
    isDisplayOptionPainScale() {
        return this._getDisplayOption() === "pain_scale";
    }

    /**
     * Bubble scale — connected, colour-coded circles that grow to fit the label written inside them.
     *
     * Named for what it draws rather than what it was first used for, unlike `pain_scale`: it
     * serves MSK activity and work impact, and any future scale whose points are worded. That
     * naming difference is the whole reason the two are separate options.
     * @returns {boolean}
     */
    isDisplayOptionBubble() {
        return this._getDisplayOption() === "bubble";
    }

    /**
     * Public getter for the raw displayOption string (e.g. "stacked", "sidebyside", "body", "row").
     * @returns {string}
     */
    getDisplayOption() {
        return this._getDisplayOption();
    }

    /**
     * Returns the i18n key for the progress-bar Next button from
     * configuration.nextButtonLabel. Falls back to null so the navigation
     * renders its default label.
     * @returns {string|null}
     */
    getNextButtonLabelKey() {
        const cfg = this._schema.configuration || {};
        return cfg.nextButtonLabel || null;
    }

    /**
     * Should picking an answer move straight on, instead of waiting for Next?
     *
     * A navigation concern, so it sits beside nextButtonLabel at the top of `configuration`
     * rather than inside `configuration.choice`, which describes how the choices are drawn.
     * It is deliberately NOT a `displayOption` value: display options choose a renderer, and a
     * question that advances on select draws exactly the same as one that does not. Encoding it
     * as, say, "pain_scale_auto" would make every isDisplayOptionPainScale() call site test two
     * strings to answer one question about layout.
     *
     * Defaults to false, which is what every question seeded before this flag existed relies on.
     * Note that the standard choice grid advances on select unconditionally and does not consult
     * this — the flag exists for renderers that would otherwise wait, such as the pain scale.
     *
     * @returns {boolean}
     */
    getAdvanceOnSelect() {
        if (!this.isQuestionTypeChoice()) {
            return false;
        }

        return (this._schema.configuration || {}).advanceOnSelect === true;
    }

    /**
     * Returns the meta associated to the Question. Because we don't have migrations for JSON structures yet,
     * persisted Question schemas will not have this property so we have to check for its existence.
     *
     * @return {Object}
     */
    get meta() {
        return this._schema.hasOwnProperty("meta") ? this._schema.meta : {};
    }

    get marketoField() {
        return this._schema ? this._schema.marketoField : null;
    }

    getFirstAnswerValue() {
        const answers = this.getAnswers();
        return answers && answers.length > 0 ? answers[0].getRawAnswer() : null;
    }

    getLastAnswerValue() {
        const answers = this.getAnswers();
        return answers && answers.length > 0 ? answers[answers.length - 1].getRawAnswer() : null;
    }
}
